'use client';

import { useCiCItemLookup } from '@dealerportal/catalog-ui/Hooks/useCiCItemLookup';
import {
  buildPdpDescription,
  buildPdpKeywords,
  buildPdpTitle,
  DEFAULT_PDP_TITLE_TEMPLATE,
  extractPdpClientData,
} from '@dealerportal/common';
import { useTranslations } from '@dealerportal/i18n/client';
import { useClientContext } from '@dealerportal/react-helpers';
import { FormatDate } from '@dealerportal/utilities';
import { addDays } from 'date-fns/addDays';
import { usePathname } from 'next/navigation';
import { ProductJsonLd } from 'next-seo';
import { useCallback, useEffect, useState } from 'react';
import { useProductLink } from '../../Hooks/useProductLink';
import { ProductDetailsClientSideSkeleton } from './ProductDetailsClientSideSkeleton';
import { ProductDetailsSwitch } from './ProductDetailsSwitch';

type Props = {
  catalogVersionId: string;
  slug: string;
  sku: string;
  primaryUrl?: string;
  isMobileDevice: boolean;
  topSectionContent: React.ReactNode;
  marketingContent: React.ReactNode;
  actonForm: React.ReactNode;
  hideBrandColorSkuTitleInPDP?: boolean;
  /**
   * Server action that renders the marketing block for another item code.
   * Used when the visitor switches colour in place, since `marketingContent`
   * was rendered for the item in the URL the page was loaded with.
   */
  loadMarketingContent?: (itemCode: string) => Promise<React.ReactNode>;
};

const toItemCode = (slug: string, sku: string) =>
  `${slug}-${sku}`.replace(/-missing-sku/g, '');

/**
 * The slug/sku the PDP shows. Picking a colour in the visualizer swaps the
 * product in place: it only replaces the URL, without a navigation, so the
 * server props keep the slug/sku the page was loaded with and the pathname
 * holds the current one.
 */
const useSelectedVariant = (slug: string, sku: string) => {
  const pathname = usePathname();
  const match = pathname?.match(/\/d\/([^/]+)\/([^/]+)\/?$/);
  if (!match) return { slug, sku };
  return {
    slug: decodeURIComponent(match[1]),
    sku: decodeURIComponent(match[2]),
  };
};

type MetaSelector = { name: string } | { property: string };

const setMetaTag = (selector: MetaSelector, content: string): (() => void) => {
  const [attr, value] =
    'name' in selector
      ? ['name', selector.name]
      : ['property', selector.property];
  const existing = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${value}"]`
  );
  if (!existing) {
    const created = document.createElement('meta');
    created.setAttribute(attr, value);
    created.setAttribute('content', content);
    document.head.appendChild(created);
    return () => created.remove();
  }
  const prev = existing.getAttribute('content');
  existing.setAttribute('content', content);
  return () => {
    if (prev === null) existing.removeAttribute('content');
    else existing.setAttribute('content', prev);
  };
};

// Same contract as setMetaTag, for the server-emitted <link rel="canonical">.
const setCanonicalLink = (href: string): (() => void) => {
  const existing = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]'
  );
  if (!existing) {
    const created = document.createElement('link');
    created.setAttribute('rel', 'canonical');
    created.setAttribute('href', href);
    document.head.appendChild(created);
    return () => created.remove();
  }
  const prev = existing.getAttribute('href');
  existing.setAttribute('href', href);
  return () => {
    if (prev === null) existing.removeAttribute('href');
    else existing.setAttribute('href', prev);
  };
};

export const ProductDetailsClientSide = (props: Props) => {
  const { primaryUrl, loadMarketingContent } = props;
  const { slug, sku } = useSelectedVariant(props.slug, props.sku);
  const itemCode = toItemCode(slug, sku);
  const initialItemCode = toItemCode(props.slug, props.sku);
  const { products, isLoading } = useCiCItemLookup([itemCode]);
  const { generateProductLink } = useProductLink();
  const [loadedMarketing, setLoadedMarketing] = useState<React.ReactNode>();

  const selectVariant = useCallback(
    (variant: { slug: string; sku: string }) => {
      const { search, hash } = window.location;
      window.history.replaceState(
        null,
        '',
        `${generateProductLink(variant)}${search}${hash}`
      );
    },
    [generateProductLink]
  );

  useEffect(() => {
    if (itemCode === initialItemCode || !loadMarketingContent) return;
    let cancelled = false;
    loadMarketingContent(itemCode)
      .then((content) => {
        if (!cancelled) setLoadedMarketing(content);
      })
      .catch((error) =>
        console.warn('Failed to load PDP marketing content:', error)
      );
    return () => {
      cancelled = true;
    };
  }, [itemCode, initialItemCode, loadMarketingContent]);

  // Until the new item's block arrives, keep the one already on screen.
  const marketingContent =
    itemCode === initialItemCode || loadedMarketing === undefined
      ? props.marketingContent
      : loadedMarketing;
  const { siteTokens, clientName } = useClientContext();
  const t = useTranslations('products');

  const product = products[0];

  useEffect(() => {
    if (!product) return;

    const clientData = extractPdpClientData({
      tokens: [
        { TokenName: 'companyname', Replacement: siteTokens.companyName },
        { TokenName: 'PrimaryMarket', Replacement: siteTokens.primaryMarket },
        { TokenName: 'all-showrooms', Replacement: siteTokens.allShowrooms },
        { TokenName: 'city1', Replacement: siteTokens.city1 },
      ],
      fallbackClientName: clientName,
    });

    // We want the raw ICU template (buildPdpTitle does the substitution);
    // `t()` would try to format with no values and return the key path.
    let template = DEFAULT_PDP_TITLE_TEMPLATE;
    try {
      const raw = t.raw('pdpTitle');
      if (typeof raw === 'string') template = raw;
    } catch {}

    const title = buildPdpTitle(product, clientData, template);
    const description = buildPdpDescription(product, clientData);
    const keywords = buildPdpKeywords(product, clientData);

    const prevTitle = document.title;
    document.title = title;
    const restorers = [
      setMetaTag({ name: 'description' }, description),
      setMetaTag({ name: 'keywords' }, keywords),
      setMetaTag({ property: 'og:title' }, title),
      setMetaTag({ property: 'og:description' }, description),
      setMetaTag({ name: 'twitter:title' }, title),
      setMetaTag({ name: 'twitter:description' }, description),
    ];

    return () => {
      document.title = prevTitle;
      for (const restore of restorers) restore();
    };
  }, [product, siteTokens, clientName, t]);

  // generateMetadata's canonical names the item the page loaded with; a swap
  // only replaces the URL, so point it at the selected variant like the JSON-LD.
  useEffect(() => {
    const origin = primaryUrl ?? window.location.origin;
    return setCanonicalLink(`${origin}/d/${slug}/${sku}`);
  }, [primaryUrl, slug, sku]);

  // While a colour swap loads, keep showing the previous product rather than
  // the skeleton, so the page (and the visualizer in it) stays mounted.
  if (isLoading && !product) {
    return <ProductDetailsClientSideSkeleton />;
  }

  // The CiC lookup can resolve with no products (e.g. the catalog version is
  // unavailable). In that case, we redirect to the search results page.
  if (!product) {
    window.location.replace('/p');
    return <ProductDetailsClientSideSkeleton />;
  }

  const {
    productName,
    brandName,
    colorName,
    UPID,
    swatches,
    description,
    unitPrice,
  } = product;

  return (
    <>
      <ProductJsonLd
        scriptId="product-json-ld"
        useAppDir={true}
        productName={productName}
        images={[
          swatches.find((swatch) => swatch?.UPID === UPID)?.imageURL ?? '',
        ]}
        description={description}
        color={colorName}
        manufacturerName={brandName}
        brand={brandName}
        sku={sku}
        productID={UPID}
        gtin="N/A"
        model="N/A"
        url={`${primaryUrl}/d/${slug}/${sku}`}
        offers={[
          {
            price: unitPrice ?? 0,
            priceCurrency: 'USD',
            priceValidUntil: FormatDate(addDays(new Date(), 1), 'yyyy-MM-dd'),
            availability: 'https://schema.org/InStock',
            url: `${primaryUrl}/d/${slug}/${sku}`,
          },
        ]}
      />
      <ProductDetailsSwitch
        productInfo={product}
        isMobileDevice={props.isMobileDevice}
        topSectionContent={props.topSectionContent}
        marketingContent={marketingContent}
        actonForm={props.actonForm}
        hideBrandColorSkuTitleInPDP={props.hideBrandColorSkuTitleInPDP}
        onVariantSelected={selectVariant}
      />
    </>
  );
};
